import { FC, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Alert } from 'antd';
import { ALERT_Z_INDEX } from '../../utils/alertManager';
import './AlertMessage.scss';

export type AlertType = 'success' | 'error' | 'info' | 'warning';

export interface AlertConfig {
  type: AlertType;
  message: string;
  closable?: boolean;
  onClose?: () => void;
}

export interface AlertMessageProps {
  config: AlertConfig | null;
  onClose: () => void;
  zIndex?: number;
}

const AUTO_HIDE_TIME   = 1500; // 1.5 秒（成功/信息/警告等）
const FORCE_HIDE_TIME  = 2000; // 2 秒兜底
const EXIT_ANIMATION_TIME = 300; // 动画耗时
// 失败类型停留时长为上述的两倍
const ERROR_AUTO_HIDE_TIME  = AUTO_HIDE_TIME * 2;  // 3 秒
const ERROR_FORCE_HIDE_TIME = FORCE_HIDE_TIME * 2; // 4 秒兜底

export const AlertMessage: FC<AlertMessageProps> = ({
  config,
  onClose,
  // 默认使用较高层级，确保在 AntD Modal（默认 z-index=1000）之上
  zIndex = ALERT_Z_INDEX.MY_WORK,
}) => {
  
  const [alertInfo, setAlertInfo] = useState<{
    type: AlertType;
    message: string;
    isExiting: boolean;
  } | null>(null);

  /* 1. config 变化时：显示 + 启动计时器 */
  useEffect(() => {
    if (!config) return;

    const isError = config.type === 'error';
    const autoHide = isError ? ERROR_AUTO_HIDE_TIME : AUTO_HIDE_TIME;
    const forceHide = isError ? ERROR_FORCE_HIDE_TIME : FORCE_HIDE_TIME;

    // 让弹窗可见
    setAlertInfo({ type: config.type, message: config.message, isExiting: false });

    // 到点后进入退出动画
    const auto = setTimeout(() => {
      setAlertInfo(prev => (prev ? { ...prev, isExiting: true } : null));
    }, autoHide);

    // 兜底关闭
    const force = setTimeout(() => {
      setAlertInfo(null);
      onClose();
    }, forceHide);

    return () => {
      clearTimeout(auto);
      clearTimeout(force);
    };
  }, [config, onClose]);

  /* 2. 退出动画完成后彻底卸载 */
  useEffect(() => {
    if (alertInfo?.isExiting) {
      const timer = setTimeout(() => {
        setAlertInfo(null);
        onClose();
      }, EXIT_ANIMATION_TIME);
      return () => clearTimeout(timer);
    }
  }, [alertInfo, onClose]);

  if (!alertInfo) return null;

  const content = (
    <div
      className={`alert-container ${alertInfo.isExiting ? 'alert-exit' : 'alert-enter'}`}
      style={{
        position: 'fixed',
        top: 20,
        left: '50%',
        zIndex,
        width: '90%',
        maxWidth: 450,
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        borderRadius: 6,
        opacity: alertInfo.isExiting ? 0 : 1,
        transform: `translateX(-50%) ${alertInfo.isExiting ? 'translateY(-20px)' : 'translateY(0)'}`,
        transition: 'all 0.3s ease-in-out',
        background: '#fff',
      }}
    >
      <Alert
        message={alertInfo.message}
        type={alertInfo.type}
        showIcon
        closable={config?.closable !== false}
        onClose={() => setAlertInfo(prev => (prev ? { ...prev, isExiting: true } : null))}
        style={{ border: 'none', borderRadius: 6 }}
      />
    </div>
  );

  // 使用 Portal 挂到 body，避免被 Modal/transform 容器的层叠上下文遮挡
  if (typeof document !== 'undefined') {
    return createPortal(content, document.body);
  }

  return content;
};